import { useState, type ReactNode } from "react";
import {
  Alert,
  Badge,
  Button,
  Field,
  Input,
  Modal,
  PasswordInput,
  type BadgeVariant,
} from "../../ds";
import type { DeviceDetail, ModemStatus } from "../../lib/api";
import { fmtUptime } from "../../lib/format";
import { useDeviceAction } from "../../lib/hooks/useDeviceAction";
import { useSection } from "../../lib/hooks/useSection";
import { KeyValues } from "../KeyValues";
import { SectionCard } from "../SectionCard";
import { SignalPanel } from "./SignalPanel";

// Modem-Karte im Tab „Internet“ → action `modem_status` + die Modem-Schreibaktionen.
// Steht unter den Uplinks: LTE ist eine Leitung von mehreren, nicht die Leitung.

// Der Registrierungstext kommt fertig formuliert von der Box („registered
// (roaming)", „registration denied"), nicht als einzelnes Schlagwort. Eine
// Tabelle mit ganzen Wörtern traf deshalb nie und färbte jeden Router grau —
// auch einen, der seit Wochen online ist. Reihenfolge zählt: „not registered"
// enthält „registered".
function regVariant(registration: string): BadgeVariant {
  const text = registration.toLowerCase();
  if (text.includes("denied")) return "danger";
  if (text.includes("not registered") || text.includes("unregistered")) return "danger";
  if (text.includes("searching")) return "warn";
  // Roaming ist hier der Normalfall, nicht die Ausnahme: die SIMs sind
  // spanische Telefónica-IoT-Karten, jedes deutsche Netz ist für sie fremd.
  if (text.includes("registered")) return "success";
  return "neutral";
}

const mono = (value: string | undefined) => (
  <span className="font-mono text-xs">{value ?? "—"}</span>
);

/** Was das Netz gerade macht — die Spalte, die man im Störungsfall zuerst liest. */
function connectionFields(modem: ModemStatus): [string, ReactNode][] {
  return [
    [
      "Registrierung",
      modem.registration ? (
        <Badge variant={regVariant(modem.registration)} dot>
          {modem.registration}
        </Badge>
      ) : (
        "—"
      ),
    ],
    ["Operator", modem.operator ?? "—"],
    ["PLMN", mono(modem.plmn)],
    ["WAN-IP", mono(modem.wanIp)],
    ["Session-Uptime", fmtUptime(modem.sessionUptimeSecs)],
  ];
}

/** Was in der Box steckt bzw. konfiguriert ist — ändert sich fast nie. */
function simFields(modem: ModemStatus): [string, ReactNode][] {
  return [
    ["APN", mono(modem.apn)],
    [
      "APN-Login",
      modem.apnUser ? `${modem.apnUser} · ${(modem.apnAuth ?? "pap").toUpperCase()}` : "—",
    ],
    ["Provider-Wahl", modem.provider ?? "—"],
    ["SIM-PIN", modem.simPin ? "hinterlegt" : "—"],
    ["ICCID", mono(modem.iccid)],
    ["IMEI", mono(modem.imei)],
  ];
}

/** Technik und Band stehen über den Funkwerten, nicht in der Werteliste. */
function TechChips({ modem }: { modem: ModemStatus }) {
  if (!modem.tech && !modem.band) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {modem.tech && <Badge variant="info">{modem.tech}</Badge>}
      {modem.band && <Badge variant="neutral">Band {modem.band}</Badge>}
    </div>
  );
}

type Dialog = "apn" | "provider" | "pin";

const DIALOG_TEXT: Record<Dialog, { title: string; subtitle: string }> = {
  apn: {
    title: "APN setzen",
    subtitle: "Schreibt APN und Zugangsdaten und bucht das Modem neu ein.",
  },
  provider: {
    title: "Provider forcen",
    subtitle: "Zwingt das Modem in ein bestimmtes Netz. „Auto“ nimmt den Zwang wieder heraus.",
  },
  pin: { title: "SIM-PIN setzen", subtitle: "Hinterlegt die PIN und bucht das Modem neu ein." },
};

// Dieselbe Regel wie sections.py::_RE_SIM_PIN — hier nur, damit „Übernehmen“
// gar nicht erst klickbar ist.
const SIM_PIN = /^[0-9]{4,8}$/;

type ApnAuth = "none" | "pap" | "chap";

/** APN samt Login. Das Passwort kennt die Oberfläche nie — leer heißt „unverändert“. */
interface ApnForm {
  apn: string;
  auth: ApnAuth;
  username: string;
  password: string;
}

// LTE kennt beim Einbuchen nur PAP und CHAP (3GPP-PCO); MS-CHAP gibt es nur bei
// PPP-Einwahl, und das EC200 nimmt in `AT+QICSGP` auch nichts anderes an.
const APN_AUTHS: { value: ApnAuth; label: string }[] = [
  { value: "none", label: "Keine" },
  { value: "pap", label: "PAP" },
  { value: "chap", label: "CHAP" },
];

function apnForm(modem: ModemStatus | null): ApnForm {
  return {
    apn: modem?.apn ?? "",
    auth: modem?.apnUser ? (modem.apnAuth ?? "pap") : "none",
    username: modem?.apnUser ?? "",
    password: "",
  };
}

/** „Keine“ nimmt den Login auf dem Router heraus (sections.py::_apn_user). */
function apnParams(form: ApnForm): Record<string, string> {
  const apn = form.apn.trim();
  if (form.auth === "none") return { apn, username: "" };
  const params = { apn, username: form.username.trim(), auth: form.auth };
  // Ohne Passwort bleibt das hinterlegte stehen (writes.py::set_apn).
  return form.password ? { ...params, password: form.password } : params;
}

function canSubmit(dialog: Dialog | null, value: string, apn: ApnForm): boolean {
  if (dialog === "apn") return !!apn.apn.trim() && (apn.auth === "none" || !!apn.username.trim());
  return dialog === "pin" ? SIM_PIN.test(value) : !!value.trim();
}

// Die Netze, die für die Flotte in Frage kommen. Freitext stand hier vorher:
// eine Ziffer daneben und der Router bucht sich nicht wieder ein — das ist eine
// Vor-Ort-Fahrt. „auto" ist der leere Wert, den der Server daraus macht
// (sections.py::_provider).
const PROVIDERS = [
  { value: "auto", label: "Auto" },
  { value: "26201", label: "Telekom" },
  { value: "26202", label: "Vodafone" },
  { value: "26203", label: "o2" },
];

function ApnInputs({
  form,
  passwordStored,
  onChange,
}: {
  form: ApnForm;
  passwordStored: boolean;
  onChange: (form: ApnForm) => void;
}) {
  const set = (patch: Partial<ApnForm>) => onChange({ ...form, ...patch });
  return (
    <>
      <Input
        label="APN"
        placeholder="internet.telekom"
        value={form.apn}
        autoFocus
        onChange={(e) => set({ apn: e.target.value })}
      />
      <Field
        label="Anmeldung"
        htmlFor="modem-apn-auth"
        helperText="Nur, wenn die SIM Zugangsdaten verlangt. Ältere Router-Images reichen sie nicht ans Modem weiter."
      >
        <select
          id="modem-apn-auth"
          className="ds-input"
          value={form.auth}
          onChange={(e) => set({ auth: e.target.value as ApnAuth })}
        >
          {APN_AUTHS.map((a) => (
            <option key={a.value} value={a.value}>
              {a.label}
            </option>
          ))}
        </select>
      </Field>
      {form.auth !== "none" && (
        <>
          <Input
            label="Benutzer"
            autoComplete="off"
            value={form.username}
            onChange={(e) => set({ username: e.target.value })}
          />
          <PasswordInput
            label="Passwort"
            autoComplete="new-password"
            helperText={
              passwordStored
                ? "Leer lassen behält das bisherige Passwort — der Router gibt es nie heraus."
                : undefined
            }
            value={form.password}
            onChange={(e) => set({ password: e.target.value })}
          />
        </>
      )}
    </>
  );
}

function DialogInput({
  dialog,
  value,
  onChange,
}: {
  dialog: Dialog | null;
  value: string;
  onChange: (v: string) => void;
}) {
  if (dialog === "pin")
    // Sichtbar statt `password`: ein Tippfehler kostet hier einen SIM-Versuch.
    return (
      <Input
        label="PIN"
        className="font-mono"
        inputMode="numeric"
        autoComplete="off"
        maxLength={8}
        value={value}
        autoFocus
        helperText="4–8 Ziffern. Falsche PIN kostet je Bring-up einen Versuch, nach 3 PUK-Sperre."
        onChange={(e) => onChange(e.target.value.trim())}
      />
    );
  return (
    <Field
      label="Netz"
      htmlFor="modem-provider"
      helperText="„Auto“ schickt einen leeren Wert — der Router erzwingt dann nichts."
    >
      <select
        id="modem-provider"
        className="ds-input"
        value={value}
        autoFocus
        onChange={(e) => onChange(e.target.value)}
      >
        {PROVIDERS.map((p) => (
          <option key={p.value} value={p.value}>
            {p.label}
          </option>
        ))}
        {/* Ein von Hand gesetztes fremdes Netz bleibt wählbar, statt beim
            Öffnen stillschweigend auf „Auto“ zu kippen. */}
        {!PROVIDERS.some((p) => p.value === value) && (
          <option value={value}>{value} (unbekannt)</option>
        )}
      </select>
    </Field>
  );
}

/** Was „Übernehmen“ an den Router schickt — und was die Rückmeldung dazu sagt. */
function dialogRequest(
  dialog: Dialog,
  value: string,
  apn: ApnForm,
): { action: string; params: Record<string, string>; okText: string } {
  const trimmed = value.trim();
  if (dialog === "apn") {
    const params = apnParams(apn);
    return {
      action: "set_apn",
      params,
      okText: params.auth
        ? `APN auf „${params.apn}“ mit ${params.auth.toUpperCase()}-Login „${params.username}“ gesetzt.`
        : `APN auf „${params.apn}“ ohne Login gesetzt.`,
    };
  }
  if (dialog === "provider")
    // „auto" macht der Server zum leeren Wert (sections.py::_provider) — die
    // Rückmeldung soll deshalb auch sagen, was passiert ist.
    return {
      action: "force_provider",
      params: { provider: trimmed },
      okText:
        trimmed === "auto"
          ? "Provider-Zwang aufgehoben — das Modem wählt wieder selbst."
          : `Netz auf ${PROVIDERS.find((p) => p.value === trimmed)?.label ?? trimmed} (${trimmed}) gezwungen.`,
    };
  return {
    action: "sim_pin_set",
    params: { pin: trimmed },
    okText: "SIM-PIN hinterlegt, Modem wird neu eingebucht.",
  };
}

/** Eingabe für APN, erzwungenes Netz oder SIM-PIN — alle mit Warnung. */
function ModemParamModal({
  dialog,
  busy,
  submittable,
  onClose,
  onSubmit,
  children,
}: {
  dialog: Dialog | null;
  busy: boolean;
  submittable: boolean;
  onClose: () => void;
  onSubmit: () => void;
  children: ReactNode;
}) {
  const text = dialog ? DIALOG_TEXT[dialog] : null;
  return (
    <Modal
      open={dialog !== null}
      onClose={onClose}
      title={text?.title}
      subtitle={text?.subtitle}
      size="sm"
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            iconName="check"
            disabled={!submittable}
            loading={busy}
            onClick={onSubmit}
          >
            Übernehmen
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Alert variant="warning">
          Die Datenverbindung bricht dabei kurz ab. Bei falschen Werten bucht sich der Router nicht
          wieder ein — dann hilft nur eine Vor-Ort-Fahrt.
        </Alert>
        {children}
      </div>
    </Modal>
  );
}

export function LteSection({ device }: { device: DeviceDetail }) {
  const section = useSection<ModemStatus>(device, "lte");
  const { busy, note, setNote, run } = useDeviceAction(device.id);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [value, setValue] = useState("");
  const [apn, setApn] = useState<ApnForm>(apnForm(null));

  const disabled = !device.online || busy !== null;

  function openDialog(next: Dialog) {
    if (next === "apn") setApn(apnForm(section.data));
    // Kein Zwang steht in der Auswahl als „Auto“ — leer wäre eine Option ohne Text.
    else if (next === "provider") setValue(section.data?.provider?.trim() || "auto");
    // Die hinterlegte PIN kennt der Server nicht (recipes.py liefert nur `simPin`).
    else setValue("");
    setDialog(next);
  }

  async function submitDialog() {
    if (dialog) {
      const { action, params, okText } = dialogRequest(dialog, value, apn);
      await run(action, params, okText);
    }
    setDialog(null);
    setValue("");
  }

  return (
    <>
      <SectionCard
        title="Mobilfunk (LTE)"
        description="Funkwerte, Registrierung, WAN-IP — eine Leitung unter den Uplinks oben"
        section={section}
        deviceOnline={device.online}
        actions={
          <>
            <Button
              size="sm"
              variant="outline"
              iconName="swap"
              disabled={disabled}
              loading={busy === "wan_reconnect"}
              onClick={() =>
                void run("wan_reconnect", undefined, "WAN wird neu verbunden (ifup wan).")
              }
            >
              WAN reconnect
            </Button>
            <Button
              size="sm"
              variant="outline"
              iconName="smartphone"
              disabled={disabled}
              loading={busy === "modem_reconnect"}
              onClick={() => void run("modem_reconnect", undefined, "Modem wird neu eingebucht.")}
            >
              Modem reconnect
            </Button>
            <Button
              size="sm"
              variant="ghost"
              iconName="edit"
              disabled={disabled}
              onClick={() => openDialog("apn")}
            >
              APN
            </Button>
            <Button
              size="sm"
              variant="ghost"
              iconName="tag"
              disabled={disabled}
              onClick={() => openDialog("provider")}
            >
              Provider
            </Button>
            <Button
              size="sm"
              variant="ghost"
              iconName="lock"
              disabled={disabled}
              onClick={() => openDialog("pin")}
            >
              SIM-PIN
            </Button>
          </>
        }
        above={
          note && (
            <Alert variant={note.variant} dismissible onDismiss={() => setNote(null)}>
              {note.text}
              {note.variant === "success" && " Neue Werte erst nach „Vom Router laden“."}
            </Alert>
          )
        }
      >
        {(modem) => (
          <div className="flex flex-col gap-4">
            <TechChips modem={modem} />
            <SignalPanel modem={modem} />
            <div className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
              <KeyValues title="Verbindung" columns={1} items={connectionFields(modem)} />
              <KeyValues title="SIM & Zugang" columns={1} items={simFields(modem)} />
            </div>
          </div>
        )}
      </SectionCard>

      <ModemParamModal
        dialog={dialog}
        busy={busy === "set_apn" || busy === "force_provider" || busy === "sim_pin_set"}
        submittable={canSubmit(dialog, value, apn)}
        onClose={() => setDialog(null)}
        onSubmit={() => void submitDialog()}
      >
        {dialog === "apn" ? (
          <ApnInputs form={apn} passwordStored={!!section.data?.apnPassword} onChange={setApn} />
        ) : (
          <DialogInput dialog={dialog} value={value} onChange={setValue} />
        )}
      </ModemParamModal>
    </>
  );
}
